// Safe wrappers around the Meta Pixel `fbq` global + server-side mirror to
// /api/pixel/event (which forwards to Meta Conversions API).
//
// Both paths use the same `event_id` so Meta dedupes correctly. When the
// caller has user info (email, phone, name, etc.), pass it as `userData` to
// enable Meta Advanced Matching \u2014 higher Event Match Quality (EMQ)
// on both the browser Pixel and the CAPI mirror.
//
// Even for anonymous events we ship as many signals as possible:
//   \u2022 _fbp   \u2014 first-party Meta browser cookie (we set one if missing)
//   \u2022 _fbc   \u2014 Facebook click ID (built from ?fbclid= URL param)
//   \u2022 external_id \u2014 stable per-visitor UUID persisted in localStorage
//   \u2022 client user agent + client IP + fbp/fbc \u2014 attached server-side.
// These push EMQ up on PageView / HomepageView / GetAccessClick even when
// the user has never given us their email or phone.

import { api } from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { hasMarketingConsent, subscribeConsent } from "@/lib/consent";

const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";
const EXTERNAL_ID_KEY = "dc_visitor_id";
const COOKIE_TTL_DAYS = 90;

const getFbq = () => (typeof window !== "undefined" ? window.fbq : undefined);

const genEventId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value, days = COOKIE_TTL_DAYS) => {
  if (typeof document === "undefined") return;
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch (e) {
    // Some sandboxes / third-party contexts block document.cookie writes.
    // Failing to persist fbp/fbc is non-fatal — the event still fires with
    // whatever cookies the browser already has — so we log and move on.
    devLog.warn(`metaPixel: writeCookie(${name}) failed`, e);
  }
};

/**
 * Meta's _fbp format: `fb.1.{creation_ms}.{random_10_digit_int}`. If the Pixel
 * SDK hasn't loaded / set the cookie yet, we set one ourselves so every event
 * carries a stable browser id.
 */
const ensureFbp = () => {
  const existing = readCookie(FBP_COOKIE);
  if (existing) return existing;
  const rand = Math.floor(1000000000 + Math.random() * 9000000000);
  const value = `fb.1.${Date.now()}.${rand}`;
  writeCookie(FBP_COOKIE, value);
  return value;
};

/**
 * If the visitor arrived from a Meta ad, the URL contains `?fbclid=XXX`. We
 * convert it into the _fbc cookie format Meta expects and persist it.
 */
const ensureFbc = () => {
  const existing = readCookie(FBC_COOKIE);
  if (existing) return existing;
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid");
  if (!fbclid) return null;
  const value = `fb.1.${Date.now()}.${fbclid}`;
  writeCookie(FBC_COOKIE, value);
  return value;
};

/**
 * Stable per-visitor identifier persisted in localStorage.
 *
 * SECURITY NOTE (intentional design): this value is a client-generated,
 * pseudo-anonymous UUID (or a random fallback). It contains NO PII,
 * NO credentials, and NO secrets. It exists purely so Meta Advanced
 * Matching can stitch multiple anonymous touchpoints from the same
 * browser into a single user profile (major EMQ boost even without
 * email/phone).
 *
 * Because Meta's browser Pixel needs to read this identifier before
 * every event fires, it MUST be accessible to page JavaScript — an
 * HttpOnly cookie would make it invisible to the pixel and defeat
 * the purpose. localStorage is the correct storage tier for
 * non-sensitive, client-readable state of this kind.
 */
const ensureExternalId = () => {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(EXTERNAL_ID_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || `dc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      window.localStorage.setItem(EXTERNAL_ID_KEY, id);
    }
    return id;
  } catch (e) {
    // Private mode / storage quota / disabled storage → skip external_id.
    // Events still fire (just without this specific signal).
    devLog.warn("metaPixel: ensureExternalId failed (storage unavailable?)", e);
    return null;
  }
};

// Initialise identifiers eagerly at module load so they're already set by
// the time the first event fires.
if (typeof window !== "undefined") {
  ensureFbp();
  ensureFbc();
  ensureExternalId();
}

const sourceUrl = () => (typeof window !== "undefined" ? window.location.href : undefined);

const normalizePhone = (raw) => (raw ? String(raw).replace(/\D/g, "") : undefined);
const normalizeLower = (raw) => (raw ? String(raw).trim().toLowerCase() : undefined);

const buildBrowserMatching = (u, persistentExternalId) => {
  const out = {};
  const email = normalizeLower(u?.email);
  const phone = normalizePhone(u?.phone);
  if (email) out.em = email;
  if (phone) out.ph = phone;
  if (u?.firstName) out.fn = normalizeLower(u.firstName);
  if (u?.lastName) out.ln = normalizeLower(u.lastName);
  if (u?.city) out.ct = normalizeLower(u.city);
  if (u?.state) out.st = normalizeLower(u.state);
  if (u?.zip) out.zp = normalizeLower(u.zip);
  if (u?.country) out.country = normalizeLower(u.country);
  if (u?.dob) out.db = String(u.dob).replace(/\D/g, "").slice(0, 8);
  const extId = u?.externalId || persistentExternalId;
  if (extId) out.external_id = String(extId);
  if (u?.fbLoginId) out.fb_login_id = String(u.fbLoginId);
  return Object.keys(out).length ? out : undefined;
};

const buildServerMatching = (u, persistentExternalId) => {
  const out = {};
  if (u?.email) out.email = String(u.email);
  if (u?.phone) out.phone = String(u.phone);
  if (u?.firstName) out.first_name = String(u.firstName);
  if (u?.lastName) out.last_name = String(u.lastName);
  if (u?.city) out.city = String(u.city);
  if (u?.state) out.state = String(u.state);
  if (u?.zip) out.zip = String(u.zip);
  if (u?.country) out.country = String(u.country);
  if (u?.dob) out.dob = String(u.dob);
  const extId = u?.externalId || persistentExternalId;
  if (extId) out.external_id = String(extId);
  if (u?.fbLoginId) out.fb_login_id = String(u.fbLoginId);
  return out;
};

const mirrorToCapi = (body) => {
  api.post("/pixel/event", body).catch((e) => devLog.warn("CAPI mirror failed", e));
};

const dispatch = ({ fbqType, name, params, userData }) => {
  // Consent gate — until the visitor grants marketing consent we drop every
  // Meta event (browser Pixel + CAPI mirror). Once consent is granted the
  // event flow resumes on subsequent user interactions.
  if (!hasMarketingConsent()) {
    devLog.log(`metaPixel: skipped '${name || fbqType}' — no marketing consent`);
    return;
  }
  const eventId = params?.event_id || genEventId();
  const enrichedParams = { ...(params || {}), event_id: eventId };
  const persistentExternalId = ensureExternalId();
  const fbp = ensureFbp();
  const fbc = ensureFbc() || readCookie(FBC_COOKIE);

  const browserMatching = buildBrowserMatching(userData || {}, persistentExternalId);

  const fbq = getFbq();
  if (typeof fbq === "function" && name) {
    try {
      const options = { eventID: eventId };
      if (browserMatching) Object.assign(options, browserMatching);
      fbq(fbqType, name, enrichedParams, options);
    } catch (e) {
      // Never let a Pixel misfire break the calling flow.
      // Meta's SDK can throw when ad blockers or Brave shields inject
      // a stub fbq that isn't fully spec-compliant. The CAPI mirror
      // below still runs, so events are not lost.
      devLog.warn(`metaPixel: browser fbq('${name}') threw`, e);
    }
  }

  mirrorToCapi({
    event_name: name,
    event_id: eventId,
    event_source_url: sourceUrl(),
    custom_data: enrichedParams,
    fbp,
    fbc,
    ...buildServerMatching(userData || {}, persistentExternalId),
  });
};

export const trackPageView = (userData) => {
  dispatch({ fbqType: "track", name: "PageView", params: {}, userData });
};

export const trackEvent = (name, params, userData) => {
  if (!name) return;
  dispatch({ fbqType: "track", name, params: params || {}, userData });
};

export const trackCustomEvent = (name, params, userData) => {
  if (!name) return;
  dispatch({ fbqType: "trackCustom", name, params: params || {}, userData });
};

// ---------------------------------------------------------------------------
// Deferred Meta Pixel bootstrap.
//
// We removed the pixel snippet from index.html so nothing tracks pre-consent.
// This function loads `fbevents.js`, calls `fbq('init', PIXEL_ID)` for EVERY
// configured pixel, and fires the initial PageView. Safe to call multiple
// times \u2014 subsequent calls short-circuit if `window.fbq` is already
// installed.
//
// Multi-pixel note: once you call `fbq('init', ...)` more than once, every
// subsequent `fbq('track', ...)` fans out to ALL initialized pixels
// automatically (Meta's SDK behaviour by design). That means the four
// tracked events (PageView / Lead / WhatsAppClick / TelegramClick) fire
// to every pixel below without any per-call changes.
// ---------------------------------------------------------------------------
const META_PIXEL_IDS = [
  "1366436505680459", // Primary — Danny Clans Media
  "892187813719913",  // Secondary — parallel measurement pixel
];
let _pixelInitialized = false;

const _installFbqStub = () => {
  if (typeof window === "undefined" || window.fbq) return;
  // Meta's official snippet, transcribed to JS. Queues calls until fbevents.js
  // finishes loading, then flushes them.
  const n = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  s.parentNode.insertBefore(t, s);
};

export const initMetaPixel = () => {
  if (_pixelInitialized) return;
  if (!hasMarketingConsent()) return;   // hard guard even if called by mistake
  _installFbqStub();
  const fbq = getFbq();
  if (typeof fbq !== "function") return;
  try {
    // Init every configured pixel BEFORE firing PageView. Meta's SDK
    // fans PageView out to all inited pixels in a single call.
    META_PIXEL_IDS.forEach((id) => {
      if (id) fbq("init", id);
    });
    fbq("track", "PageView");
    _pixelInitialized = true;
    devLog.log(
      `metaPixel: initialized post-consent (${META_PIXEL_IDS.length} pixel${META_PIXEL_IDS.length === 1 ? "" : "s"})`,
    );
  } catch (err) {
    devLog.warn("metaPixel: init failed", err);
  }
};

/**
 * Bootstrap called once from `App.js`. If the visitor has already granted
 * marketing consent on a previous visit, we init immediately. Either way we
 * subscribe to future consent changes so switching from Reject → Accept
 * inside Cookie preferences installs the pixel on the fly.
 */
export const bootstrapMetaPixelWithConsent = () => {
  if (hasMarketingConsent()) initMetaPixel();
  subscribeConsent((c) => {
    if (c.marketing) initMetaPixel();
  });
};
